import { type PayloadAction, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import { addPrimitiveCases } from "../../../redux/shared/add-primitive-cases.ts"
import {
  afterFailedFetch,
  afterFetch,
  SCROLL_PAGE_SIZE
} from "../../../redux/shared/paged-list.ts"
import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import type { RehydrateAction } from "../../../store.ts"
import { getEntityName } from "../../../utils/user/get-entity-name"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  FINANCE_ITEM_TYPES,
  type FinanceItemTypes
} from "../ProjectViewPage/constants/finance-item-constants.ts"
import type {
  FinancialTransactionItem,
  FinancialTransactionSummary
} from "../types/project-finance.types.tsx"
import type { ProjectInvoice } from "../types/project-invoices.types.tsx"
import { type Project } from "../types/project.types.ts"
import {
  deleteChecklistItem,
  fetchChecklistItems,
  fetchChecklistItemsFilterOptions
} from "./project-checklist/project-checklist.thunks.ts"
import {
  fetchProjectCommissions,
  patchProjectCommission
} from "./project-commissions/project-commissions.thunks.ts"
import {
  deleteFinancialTransactionItem,
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  setFinancialAlertsMuted,
  fetchFinancialTransactionItems,
  patchFinancialTransactionItem
} from "./project-finance/project-finance.thunks.ts"
import {
  deleteInvoice,
  fetchInvoiceSnapshot,
  fetchInvoices,
  patchInvoice
} from "./project-invoices/project-invoices.thunks.ts"
import {
  createMilestoneDependency,
  deleteMilestoneDependency,
  fetchProjectMilestoneDependencies,
  patchMilestoneDependency
} from "./project-milestones/project-milestone-dependencies.thunks.ts"
import {
  createProjectMilestone,
  deleteProjectMilestone,
  fetchAlertContributingMilestones,
  fetchProjectMilestones,
  fetchProjectMilestonesFilterOptions,
  patchProjectMilestone
} from "./project-milestones/project-milestones.thunks.ts"
import { fetchProjectSubcontractors } from "./project-subcontractors/project-subcontractors.thunks.ts"
import {
  initialFinancialTransactionFilters,
  projectSliceName
} from "./project.constants.ts"
import {
  addProjectTypeToProject,
  createAndAssignSubcontractor,
  createProject,
  fetchProject,
  fetchProjectLogHistory,
  removeProjectTypeToProject,
  reorderProjectTypes,
  updateProject,
  updateProjectTypeToProject
} from "./project.thunks.ts"
import { FETCHING_SNAPSHOT_ENUM, type ProjectState } from "./project.types.ts"

function parseValue(value: FinancialTransactionItem["value"]) {
  return parseFloat(value ?? 0)
}

function updateSummary(
  summary: FinancialTransactionSummary,
  itemType: FinanceItemTypes,
  delta: number
) {
  if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE) {
    summary.receivable += delta
  } else if (itemType === FINANCE_ITEM_TYPES.PAYABLE) {
    summary.payable += delta
  }
}

const initialState: ProjectState = {
  project: null,

  milestones: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  milestonesRequestId: null,
  milestonesFilters: {
    page: 1,
    page_size: SCROLL_PAGE_SIZE,
    project_type: null,
    project_type_assignment: null
  },
  milestonesFilterOptions: {},
  milestoneDependencies: [],

  financialTransactionItems: {
    count: 0,
    results: [],
    next: null,
    previous: null,
    summary: {
      payable: 0,
      receivable: 0,
      receivable_collected: 0,
      payable_paid: 0
    }
  },
  financialTransactionItemsRequestId: null,
  financialTransactionFilters: initialFinancialTransactionFilters,
  financialTransactionSnapshot: null,
  financialAlerts: null,
  financialAlertsMuteRequestId: null,
  financialSnapshotRequestedFor: null,
  financialAlertsRequestedFor: null,
  alertContributingMilestones: [],

  invoices: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  invoicesRequestId: null,
  invoicesFilters: {
    page: 1,
    page_size: 15,
    q: null,
    type: null,
    status: null
  },
  invoiceSnapshot: null,

  subcontractors: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  subcontractorsRequestId: null,

  subcontractorsFilters: { page: 1, page_size: 15, q: null },
  selectedSubcontractor: null,

  checklistItems: {
    count: 0,
    results: [],
    next: null,
    previous: null
  },
  checklistItemsRequestId: null,
  checklistItemsFilters: {
    page: 1,
    page_size: 15,
    q: null
  },
  checklistItemsFilterOptions: {
    project_phases: [],
    status: [],
    subcontractors: [],
    team_members: [],
    admins: [],
    project_managers: [],
    priority: [],
    project_type: [],
    milestones: []
  },

  logs: [],

  commissions: {
    rows: [],
    summary: {
      office_lead_cost_plan: 0,
      total_lead_cost_plan: 0,
      office_lead_cost_actual: 0,
      others_lead_cost_actual: 0,
      others_lead_cost_plan: 0,
      referral_lead_cost_actual: 0,
      referral_lead_cost_plan: 0,
      total_lead_cost_actual: 0,
      sales_commission_actual: 0,
      sales_commission_plan: 0
    }
  },
  commissionsItemsFilters: {
    page: 1,
    page_size: 10,
    q: null
  },

  showSnapshot: false,
  financialAlertFocusRule: null,
  fetching: false,
  loadError: null,
  fetchingSection: false,
  fetchingSnapshotMap: {
    [FETCHING_SNAPSHOT_ENUM.FINANCIAL]: false,
    [FETCHING_SNAPSHOT_ENUM.INVOICE]: false,
    [FETCHING_SNAPSHOT_ENUM.MILESTONES]: false
  },
  fetchingSubcontractors: false,
  saveResult: null,
  saveFetching: false
}

const projectSlice = createSlice({
  name: projectSliceName,
  initialState,
  reducers: {
    projectSetProp: generateSetPropReducer<ProjectState>(),
    projectInvoiceUpdated: (state, action: PayloadAction<ProjectInvoice>) => {
      const invoiceIndex = state.invoices.results.findIndex(
        (invoice) => invoice.id === action.payload.id
      )
      if (invoiceIndex === -1) return

      const statusFilter = state.invoicesFilters.status
      if (statusFilter && action.payload.status !== statusFilter) {
        state.invoices.results.splice(invoiceIndex, 1)
        state.invoices.count = Math.max(0, state.invoices.count - 1)
        return
      }

      state.invoices.results[invoiceIndex] = {
        ...state.invoices.results[invoiceIndex],
        ...action.payload
      }
    },
    // Applied before the reorder request so the tabs settle where they were
    // dropped instead of snapping back until the response lands.
    projectSetProjectTypes: (
      state,
      action: PayloadAction<Project["project_type_details"]>
    ) => {
      if (state?.project) {
        state.project.project_type_details = action.payload || []
      }
    },
    // Same optimistic treatment for milestone rows dragged in the list.
    projectSetMilestoneOrder: (
      state,
      action: PayloadAction<ProjectState["milestones"]["results"]>
    ) => {
      state.milestones.results = action.payload || []
    },
    projectSetChecklistOrder: (
      state,
      action: PayloadAction<ProjectState["checklistItems"]["results"]>
    ) => {
      state.checklistItems.results = action.payload || []
    }
  },
  extraReducers: (builder) => {
    addPrimitiveCases(builder, createProject, "saveFetching")
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const status = action?.payload?.project
      if (!status) return state

      return { ...status, selectedSubcontractor: null }
    })
    builder
      // fetchProjectMilestones
      .addCase(fetchProjectMilestones.pending, (state, action) => {
        state.fetchingSection = true
        state.milestonesRequestId = action.meta.requestId

        // A refresh re-reads the rows already on screen: emptying the list
        // first would unmount them and take the scroll position with them.
        if (!action?.meta?.arg?.isMore && !action?.meta?.arg?.refresh) {
          state.milestones = initialState.milestones
        }
      })
      .addCase(fetchProjectMilestones.fulfilled, (state, action) => {
        if (state.milestonesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.milestonesRequestId = null

        state.milestones = afterFetch(
          state.milestones,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchProjectMilestones.rejected, (state, action) => {
        if (state.milestonesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.milestonesRequestId = null
        if (action?.meta?.arg?.refresh) return
        state.milestones = afterFailedFetch(
          state.milestones,
          initialState.milestones,
          action?.meta?.arg?.isMore
        )
      })

      // updateProject
      .addCase(updateProject.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.saveFetching = false
        state.project = action?.payload
      })
      .addCase(updateProject.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchProjectMilestonesFilterOptions
      .addCase(
        fetchProjectMilestonesFilterOptions.fulfilled,
        (state, action) => {
          state.milestonesFilterOptions = action?.payload
        }
      )
      .addCase(fetchProjectMilestonesFilterOptions.rejected, (state) => {
        state.milestonesFilterOptions = initialState.milestonesFilterOptions
      })

      // fetchProject
      .addCase(fetchProject.pending, (state) => {
        state.project = null
        state.fetching = true
        state.loadError = null
        state.financialTransactionSnapshot = null
        state.financialAlerts = null
        state.financialSnapshotRequestedFor = null
        state.financialAlertsRequestedFor = null
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        const project = action?.payload

        state.fetching = false
        state.project = project
        state.milestonesFilters = {
          ...initialState.milestonesFilters,
          project_type: project?.project_type_details?.[0]?.id,
          project_type_assignment:
            project?.project_type_details?.[0]?.assignment_id
        }

        state.financialTransactionFilters =
          initialState.financialTransactionFilters
        state.invoicesFilters = initialState.invoicesFilters
        state.checklistItemsFilters = initialState.checklistItemsFilters
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.fetching = false
        state.project = null
        state.loadError = {
          id: action.meta.arg,
          notFound:
            (action.payload as { status?: number } | undefined)?.status === 404
        }
      })

      // createProjectMilestone
      .addCase(createProjectMilestone.fulfilled, (state, action) => {
        // const isFull =
        //   state.milestones.results.length >= state.milestonesFilters.page_size

        state.milestones.count = state.milestones.count + 1

        if (!state?.milestones?.next) {
          state.milestones.results.push(action?.payload)
        }

        // state.milestones = {
        //   ...state.milestones,
        //   count: (state.milestones.count || 0) + 1,
        //   results:
        // isFull
        // ? state.milestones.results
        // : [...state.milestones.results, action.payload]
        // }
      })

      .addCase(patchProjectMilestone.fulfilled, (state, action) => {
        state.milestones = {
          ...state?.milestones,
          results: state?.milestones?.results?.map((m) =>
            m?.id === action?.payload?.id ? action?.payload : m
          )
        }
      })

      //deleteProjectMilestone
      .addCase(deleteProjectMilestone.fulfilled, (state, action) => {
        state.milestones.count = state.milestones.count - 1
        state.milestones = {
          ...state?.milestones,
          results: state?.milestones?.results?.filter(
            (m) => m?.id !== action?.payload
          )
        }
      })

      .addCase(fetchProjectMilestoneDependencies.fulfilled, (state, action) => {
        state.milestoneDependencies = action.payload
      })

      .addCase(createMilestoneDependency.fulfilled, (state, action) => {
        state.milestoneDependencies.push(action.payload)
      })

      .addCase(patchMilestoneDependency.fulfilled, (state, action) => {
        state.milestoneDependencies = state.milestoneDependencies.map((d) =>
          d.id === action.payload.id ? action.payload : d
        )
      })

      .addCase(deleteMilestoneDependency.fulfilled, (state, action) => {
        state.milestoneDependencies = state.milestoneDependencies.filter(
          (d) => d.id !== action.payload
        )
      })

      .addCase(patchFinancialTransactionItem.fulfilled, (state, action) => {
        const updatedItem = action.payload
        const items = state.financialTransactionItems
        if (!items) return

        const oldItem = items.results?.find((m) => m.id === updatedItem.id)

        if (oldItem && items.summary) {
          updateSummary(
            items.summary,
            oldItem.item_type,
            -parseValue(oldItem.value)
          )
          updateSummary(
            items.summary,
            updatedItem.item_type,
            parseValue(updatedItem.value)
          )
        }

        items.results = items.results?.map((m) =>
          m.id === updatedItem.id ? updatedItem : m
        )
      })

      // fetchFinancialTransactionItems
      .addCase(fetchFinancialTransactionItems.pending, (state, action) => {
        state.fetchingSection = true
        state.financialTransactionItemsRequestId = action.meta.requestId

        if (!action?.meta?.arg?.isMore) {
          state.financialTransactionItems =
            initialState.financialTransactionItems
        }
      })
      .addCase(fetchFinancialTransactionItems.fulfilled, (state, action) => {
        if (state.financialTransactionItemsRequestId !== action.meta.requestId)
          return
        state.fetchingSection = false
        state.financialTransactionItemsRequestId = null

        state.financialTransactionItems = afterFetch(
          state.financialTransactionItems,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchFinancialTransactionItems.rejected, (state, action) => {
        if (state.financialTransactionItemsRequestId !== action.meta.requestId)
          return
        state.fetchingSection = false
        state.financialTransactionItemsRequestId = null
        state.financialTransactionItems = afterFailedFetch(
          state.financialTransactionItems,
          initialState.financialTransactionItems,
          action?.meta?.arg?.isMore
        )
      })

      .addCase(deleteFinancialTransactionItem.fulfilled, (state, action) => {
        const id = action.payload
        const items = state.financialTransactionItems
        if (!items) return

        const index = items.results?.findIndex((m) => m.id === id)
        if (index === -1 || index === undefined) return

        const item = items.results[index]

        if (items.summary) {
          updateSummary(items.summary, item.item_type, -parseValue(item.value))
        }

        items.results.splice(index, 1)
      })

      // fetchInvoices
      .addCase(fetchInvoices.pending, (state, action) => {
        state.fetchingSection = true
        state.invoicesRequestId = action.meta.requestId

        if (!action?.meta?.arg?.isMore) {
          state.invoices = initialState.invoices
        }
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        if (state.invoicesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.invoicesRequestId = null

        state.invoices = afterFetch(
          state.invoices,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        if (state.invoicesRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.invoicesRequestId = null
        state.invoices = afterFailedFetch(
          state.invoices,
          initialState.invoices,
          action?.meta?.arg?.isMore
        )
      })

      // .addCase(createInvoice.fulfilled, (state, action) => {
      //   // because here, when changing the filter, there will be a data rerequest
      //   if (state.invoicesFilters.type || state.invoicesFilters.status) {
      //     state.invoicesFilters.type = initialState.invoicesFilters.type
      //     state.invoicesFilters.status = initialState.invoicesFilters.status
      //   } else {
      //     state.invoices = {
      //       ...state?.invoices,
      //       count: (state?.invoices?.count || 0) + 1,
      //       results: [action?.payload, ...(state?.invoices?.results || [])]
      //     }
      //   }
      // })

      .addCase(patchInvoice.fulfilled, (state, action) => {
        state.invoices = {
          ...state?.invoices,
          results: state?.invoices?.results?.map((m) =>
            m?.id === action?.payload?.id ? action?.payload : m
          )
        }
      })

      // deleteInvoice
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = {
          ...state?.invoices,
          results: state?.invoices?.results?.filter(
            (m) => m?.id !== action?.payload
          )
        }
      })

      // createNewProjectSubcontractor
      .addCase(createAndAssignSubcontractor.fulfilled, (state, action) => {
        const newSubcontractor = action.payload
        const label = getEntityName(newSubcontractor, newSubcontractor?.email)

        const isExist =
          state?.checklistItemsFilterOptions?.subcontractors?.find(
            (s) => s?.value?.id === newSubcontractor?.id
          )

        // because if we try to add an existing one, then a resolved promise is returned (ok = true)
        if (!isExist) {
          state.checklistItemsFilterOptions.subcontractors = [
            {
              label,
              value: {
                id: newSubcontractor?.id,
                contact_user: newSubcontractor?.contact_user
              }
            },
            ...(state.checklistItemsFilterOptions.subcontractors || [])
          ]
        }
      })

      // fetchProjectSubcontractors
      .addCase(fetchProjectSubcontractors.pending, (state, action) => {
        state.fetchingSubcontractors = true
        state.subcontractorsRequestId = action.meta.requestId

        if (!action?.meta?.arg?.isMore) {
          state.subcontractors = initialState.subcontractors
        }
      })
      .addCase(fetchProjectSubcontractors.fulfilled, (state, action) => {
        if (state.subcontractorsRequestId !== action.meta.requestId) return
        state.fetchingSubcontractors = false
        state.subcontractorsRequestId = null

        state.subcontractors = afterFetch(
          state.subcontractors,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchProjectSubcontractors.rejected, (state, action) => {
        if (state.subcontractorsRequestId !== action.meta.requestId) return
        state.fetchingSubcontractors = false
        state.subcontractorsRequestId = null
        state.subcontractors = afterFailedFetch(
          state.subcontractors,
          initialState.subcontractors,
          action?.meta?.arg?.isMore
        )
      })

      // fetchTasks
      .addCase(fetchChecklistItems.pending, (state, action) => {
        state.fetchingSection = true
        state.checklistItemsRequestId = action.meta.requestId

        if (!action?.meta?.arg?.isMore) {
          state.checklistItems = initialState.checklistItems
        }
      })
      .addCase(fetchChecklistItems.fulfilled, (state, action) => {
        if (state.checklistItemsRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.checklistItemsRequestId = null

        state.checklistItems = afterFetch(
          state.checklistItems,
          action?.payload.data,
          action?.payload?.isMore
        )
      })
      .addCase(fetchChecklistItems.rejected, (state, action) => {
        if (state.checklistItemsRequestId !== action.meta.requestId) return
        state.fetchingSection = false
        state.checklistItemsRequestId = null
        state.checklistItems = afterFailedFetch(
          state.checklistItems,
          initialState.checklistItems,
          action?.meta?.arg?.isMore
        )
      })

      // fetchChecklistItemsFilterOptions
      .addCase(fetchChecklistItemsFilterOptions.fulfilled, (state, action) => {
        state.checklistItemsFilterOptions = action?.payload
      })
      .addCase(fetchChecklistItemsFilterOptions.rejected, (state) => {
        state.checklistItemsFilterOptions =
          initialState.checklistItemsFilterOptions
      })

      // Replacing the row in place instead of refetching drops the fields the
      // write response omits (project_id, project_name, attachments) and leaves
      // it sitting in a filter it no longer matches.
      // // patchChecklistItem
      // .addCase(patchChecklistItem.fulfilled, (state, action) => {
      //   state.checklistItems = {
      //     ...state?.checklistItems,
      //     results: state?.checklistItems?.results?.map((m) =>
      //       m?.id === action?.payload?.id ? action?.payload : m
      //     )
      //   }
      // })

      //deleteChecklistItem
      .addCase(deleteChecklistItem.fulfilled, (state, action) => {
        state.checklistItems = {
          ...state?.checklistItems,
          results: state?.checklistItems?.results?.filter(
            (m) => m?.id !== action?.payload
          )
        }
      })

      // // createChecklistItem
      // .addCase(createChecklistItem.fulfilled, (state, action) => {
      //   state.checklistItems = {
      //     ...state?.checklistItems,
      //     count: (state?.checklistItems?.count || 0) + 1,
      //     results: [action?.payload, ...(state?.checklistItems?.results || [])]
      //   }
      // })

      // fetchProjectCommissions
      .addCase(fetchProjectCommissions.fulfilled, (state, action) => {
        state.commissions = action?.payload
      })
      .addCase(fetchProjectCommissions.rejected, (state) => {
        state.commissions = initialState.commissions
      })

      // patchProjectCommission
      .addCase(patchProjectCommission.fulfilled, (state, action) => {
        state.commissions = action?.payload
      })

      // addProjectTypeToProject
      .addCase(addProjectTypeToProject.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // updateProjectTypeToProject
      .addCase(updateProjectTypeToProject.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // removeProjectTypeToProject
      .addCase(removeProjectTypeToProject.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // reorderProjectTypes
      .addCase(reorderProjectTypes.fulfilled, (state, action) => {
        if (state?.project) {
          state.project.project_type_details = action?.payload || []
        }
      })

      // fetchFinancialAlerts
      .addCase(fetchFinancialAlerts.pending, (state, action) => {
        state.financialAlertsRequestedFor = action.meta.arg
      })
      .addCase(fetchFinancialAlerts.fulfilled, (state, action) => {
        state.financialAlerts = action.payload
      })
      .addCase(fetchFinancialAlerts.rejected, (state) => {
        state.financialAlerts = null
        state.financialAlertsRequestedFor = null
      })
      .addCase(fetchAlertContributingMilestones.fulfilled, (state, action) => {
        state.alertContributingMilestones = action.payload
      })
      // The switch reads this flag, so it moves on the click rather than a
      // round-trip later; a failure puts it back. Only the newest toggle may
      // write, or a slower earlier one lands last and wins.
      .addCase(setFinancialAlertsMuted.pending, (state, action) => {
        state.financialAlertsMuteRequestId = action.meta.requestId
        if (state.financialAlerts) {
          state.financialAlerts.muted = action.meta.arg.muted
        }
      })
      .addCase(setFinancialAlertsMuted.fulfilled, (state, action) => {
        if (state.financialAlertsMuteRequestId !== action.meta.requestId) return
        state.financialAlertsMuteRequestId = null
        if (state.financialAlerts) {
          state.financialAlerts.muted = action.payload
        }
      })
      .addCase(setFinancialAlertsMuted.rejected, (state, action) => {
        if (state.financialAlertsMuteRequestId !== action.meta.requestId) return
        state.financialAlertsMuteRequestId = null
        if (state.financialAlerts) {
          state.financialAlerts.muted = !action.meta.arg.muted
        }
      })

      // fetchFinancialSnapshot
      .addCase(fetchFinancialSnapshot.pending, (state, action) => {
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = true
        state.financialSnapshotRequestedFor = action.meta.arg
      })
      .addCase(fetchFinancialSnapshot.fulfilled, (state, action) => {
        state.financialTransactionSnapshot = action.payload
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = false
      })
      .addCase(fetchFinancialSnapshot.rejected, (state) => {
        state.financialTransactionSnapshot =
          initialState.financialTransactionSnapshot
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.FINANCIAL] = false
        state.financialSnapshotRequestedFor = null
      })

      // fetchInvoiceSnapshot
      .addCase(fetchInvoiceSnapshot.pending, (state) => {
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = true
      })
      .addCase(fetchInvoiceSnapshot.fulfilled, (state, action) => {
        state.invoiceSnapshot = action.payload
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = false
      })
      .addCase(fetchInvoiceSnapshot.rejected, (state) => {
        state.invoiceSnapshot = initialState.invoiceSnapshot
        state.fetchingSnapshotMap[FETCHING_SNAPSHOT_ENUM.INVOICE] = false
      })

      // fetchProjectLogHistory
      .addCase(fetchProjectLogHistory.pending, (state) => {
        state.fetchingSection = true
        state.logs = initialState.logs
      })
      .addCase(fetchProjectLogHistory.fulfilled, (state, action) => {
        state.fetchingSection = false
        state.logs = action.payload
      })
      .addCase(fetchProjectLogHistory.rejected, (state) => {
        state.fetchingSection = false
        state.logs = initialState.logs
      })

      .addCase(logout.fulfilled, () => {
        console.log("project logout")
        return initialState
      })
  }
})
export const {
  projectSetProp,
  projectInvoiceUpdated,
  projectSetProjectTypes,
  projectSetMilestoneOrder,
  projectSetChecklistOrder
} = projectSlice.actions

export default projectSlice
